/**
 * Structural update utilities for immutable state management without deep cloning
 * Provides performance-optimized alternatives to copyObject for assessment updates
 */

import { AssessmentCategory } from '../Models/Assessment/AssessmentCategory.model';
import { CommentEntry } from '../Models/Assessment/CommentEntry.model';
import { SelectionType } from '../Enum/SelectionTypeEnum';

/**
 * Update a single question's option selection using structural sharing
 * Only creates new objects for the changed path, reuses everything else
 */
export function updateQuestionOption(category: AssessmentCategory, questionIndex: number, optionIndex: number, isSelected: boolean): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const visibleOptions = question.Options?.filter((o) => !o.IsArchived) || [];

    const updatedOptions = question.Options?.map((option, idx) => {
        // Skip archived options
        if (option.IsArchived) return option;

        // Find index in visible options
        const visibleIdx = visibleOptions.indexOf(option);
        if (visibleIdx === -1) return option;

        // Only update if this is the changed option or needs to be deselected
        const shouldBeSelected = visibleIdx === optionIndex ? isSelected : false;
        if (option.IsSelected === shouldBeSelected) return option;

        return { ...option, IsSelected: shouldBeSelected };
    });

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions, IsAIAnswer: false } : q)),
    };
}

/**
 * Update suboption selection using structural sharing
 */
export function updateSubOption(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subOptionIndex: number,
    isSelected: boolean
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];

    if (!option?.SubOptions || option.SubOptions.length === 0) return category;

    const subOptionSelectionType = option.SubOptions[0].SelectionType;
    const isMultiSelect = subOptionSelectionType === SelectionType.MultiSelect;

    const updatedSubOptions = option.SubOptions.map((subOption, idx) => {
        if (isMultiSelect) {
            // Toggle only the clicked one
            if (idx === subOptionIndex && subOption.IsSelected !== isSelected) {
                return { ...subOption, IsSelected: isSelected };
            }
            return subOption;
        } else {
            // Single select: only the clicked one should be selected
            const shouldBeSelected = idx === subOptionIndex ? isSelected : false;
            if (subOption.IsSelected === shouldBeSelected) return subOption;
            return { ...subOption, IsSelected: shouldBeSelected };
        }
    });

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubOptions: updatedSubOptions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions, IsAIAnswer: false } : q)),
    };
}

/**
 * Update sub-suboption selection using structural sharing
 */
export function updateSubSubOption(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subOptionIndex: number,
    subSubOptionIndex: number,
    isSelected: boolean
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];
    const subOption = option?.SubOptions?.[subOptionIndex];

    if (!subOption?.SubOptions) return category;

    const subSubOptionSelectionType = subOption.SubOptions[0]?.SelectionType;
    const isMultiSelect = subSubOptionSelectionType === SelectionType.MultiSelect;

    const updatedSubSubOptions = subOption.SubOptions.map((sso, idx) => {
        if (isMultiSelect) {
            if (idx === subSubOptionIndex && sso.IsSelected !== isSelected) {
                return { ...sso, IsSelected: isSelected };
            }
            return sso;
        } else {
            const shouldBeSelected = idx === subSubOptionIndex ? isSelected : false;
            if (sso.IsSelected === shouldBeSelected) return sso;
            return { ...sso, IsSelected: shouldBeSelected };
        }
    });

    const updatedSubOptions = option.SubOptions?.map((sub, idx) => (idx === subOptionIndex ? { ...sub, SubOptions: updatedSubSubOptions } : sub));

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubOptions: updatedSubOptions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions, IsAIAnswer: false } : q)),
    };
}

/**
 * Update subquestion option selection using structural sharing
 */
export function updateSubQuestionOption(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subQuestionIndex: number,
    subQuestionOptionIndex: number,
    isSelected: boolean
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];
    const subQuestion = option?.SubQuestions?.[subQuestionIndex];

    if (!subQuestion?.Options) return category;

    const selectionType = subQuestion.SelectionType || SelectionType.SingleSelect;
    const isMultiSelect = selectionType === SelectionType.MultiSelect || selectionType === SelectionType.MultiSelectWithDropdown;

    const updatedSubQuestionOptions = subQuestion.Options.map((subQOpt, idx) => {
        if (isMultiSelect) {
            if (idx === subQuestionOptionIndex && subQOpt.IsSelected !== isSelected) {
                return { ...subQOpt, IsSelected: isSelected };
            }
            return subQOpt;
        } else {
            const shouldBeSelected = idx === subQuestionOptionIndex ? isSelected : false;
            if (subQOpt.IsSelected === shouldBeSelected) return subQOpt;
            return { ...subQOpt, IsSelected: shouldBeSelected };
        }
    });

    const updatedSubQuestions = option.SubQuestions?.map((sq, idx) =>
        idx === subQuestionIndex ? { ...sq, Options: updatedSubQuestionOptions, IsAIAnswer: false } : sq
    );

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubQuestions: updatedSubQuestions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions } : q)),
    };
}

/**
 * Update dropdown answer (single or multi-select) using structural sharing
 */
export function updateDropdownAnswer(category: AssessmentCategory, questionIndex: number, selectedValue: string | string[]): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const isMultiSelect = question.SelectionType === SelectionType.MultiSelectWithDropdown;

    const updatedOptions = question.Options?.map((option) => {
        const shouldBeSelected = isMultiSelect ? (selectedValue as string[]).includes(option.Name) : option.Name === selectedValue;

        if (option.IsSelected === shouldBeSelected) return option;
        return { ...option, IsSelected: shouldBeSelected };
    });

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions, IsAIAnswer: false } : q)),
    };
}

/**
 * Update question date using structural sharing
 */
export function updateQuestionDate(category: AssessmentCategory, questionIndex: number, date: string | null): AssessmentCategory {
    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Date: date, IsAIAnswer: false } : q)),
    };
}

/**
 * Update question answer text using structural sharing
 */
export function updateQuestionAnswerText(category: AssessmentCategory, questionIndex: number, answerText: string): AssessmentCategory {
    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, AnswerText: answerText, IsAIAnswer: false } : q)),
    };
}

/**
 * Update category comments using structural sharing
 */
export function updateCategoryComments(category: AssessmentCategory, comments: CommentEntry[]): AssessmentCategory {
    return { ...category, CommentsList: comments };
}

/**
 * Update SubOption text answer (for Text SelectionType) using structural sharing
 */
export function updateSubOptionTextAnswer(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subOptionIndex: number,
    textAnswer: string
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];

    if (!option?.SubOptions?.[subOptionIndex]) return category;

    const updatedSubOptions = option.SubOptions.map((subOption, idx) => (idx === subOptionIndex ? { ...subOption, TextAnswer: textAnswer } : subOption));

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubOptions: updatedSubOptions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions, IsAIAnswer: false } : q)),
    };
}

/**
 * Update SubSubOption text answer using structural sharing
 */
export function updateSubSubOptionTextAnswer(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subOptionIndex: number,
    subSubOptionIndex: number,
    textAnswer: string
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];
    const subOption = option?.SubOptions?.[subOptionIndex];

    if (!subOption?.SubOptions?.[subSubOptionIndex]) return category;

    const updatedSubSubOptions = subOption.SubOptions.map((sso, idx) => (idx === subSubOptionIndex ? { ...sso, TextAnswer: textAnswer } : sso));

    const updatedSubOptions = option.SubOptions!.map((so, idx) => (idx === subOptionIndex ? { ...so, SubOptions: updatedSubSubOptions } : so));

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubOptions: updatedSubOptions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions, IsAIAnswer: false } : q)),
    };
}

/**
 * Update SubQuestion answer text using structural sharing
 */
export function updateSubQuestionAnswerText(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subQuestionIndex: number,
    answerText: string | Date
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];

    if (!option?.SubQuestions?.[subQuestionIndex]) return category;

    const textValue = answerText instanceof Date ? answerText.toISOString() : answerText;

    const updatedSubQuestions = option.SubQuestions.map((sq, idx) =>
        idx === subQuestionIndex ? { ...sq, AnswerText: textValue, IsAIAnswer: false } : sq
    );

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubQuestions: updatedSubQuestions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions } : q)),
    };
}

/**
 * Update SubQuestion Option SubOption text answer using structural sharing
 */
export function updateSubQuestionOptionSubOptionTextAnswer(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subQuestionIndex: number,
    subQuestionOptionIndex: number,
    subOptionIndex: number,
    textAnswer: string
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];
    const subQuestion = option?.SubQuestions?.[subQuestionIndex];
    const subQuestionOption = subQuestion?.Options?.[subQuestionOptionIndex];

    if (!subQuestionOption?.SubOptions?.[subOptionIndex]) return category;

    const updatedSubOptions = subQuestionOption.SubOptions.map((so, idx) => (idx === subOptionIndex ? { ...so, TextAnswer: textAnswer } : so));

    const updatedSubQuestionOptions = subQuestion!.Options!.map((sqo, idx) =>
        idx === subQuestionOptionIndex ? { ...sqo, SubOptions: updatedSubOptions } : sqo
    );

    const updatedSubQuestions = option!.SubQuestions!.map((sq, idx) => (idx === subQuestionIndex ? { ...sq, Options: updatedSubQuestionOptions, IsAIAnswer: false } : sq));

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubQuestions: updatedSubQuestions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions, IsAIAnswer: false } : q)),
    };
}

/**
 * Update SubOption SubQuestion answer text using structural sharing
 */
export function updateSubOptionSubQuestionAnswerText(
    category: AssessmentCategory,
    questionIndex: number,
    optionIndex: number,
    subOptionIndex: number,
    subQuestionIndex: number,
    answerText: string
): AssessmentCategory {
    const question = category.Questions[questionIndex];
    const option = question.Options?.[optionIndex];
    const subOption = option?.SubOptions?.[subOptionIndex];

    if (!subOption?.SubQuestions?.[subQuestionIndex]) return category;

    const updatedSubQuestions = subOption.SubQuestions.map((sq, idx) =>
        idx === subQuestionIndex ? { ...sq, AnswerText: answerText, IsAIAnswer: false } : sq
    );

    const updatedSubOptions = option.SubOptions.map((so, idx) => (idx === subOptionIndex ? { ...so, SubQuestions: updatedSubQuestions } : so));

    const updatedOptions = question.Options?.map((opt, idx) => (idx === optionIndex ? { ...opt, SubOptions: updatedSubOptions } : opt));

    return {
        ...category,
        Questions: category.Questions.map((q, idx) => (idx === questionIndex ? { ...q, Options: updatedOptions } : q)),
    };
}
